import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  CalendarToday,
  CheckCircleOutline,
  Download,
  ErrorOutline,
  HourglassEmpty,
  InsertChartOutlined,
  SwapHoriz,
} from '@mui/icons-material';
import { caseService } from '../services/api';
import { downloadCasesCsv, filterCasesByDate, toDateInput } from '../utils/reporting';
import { usePortalText } from '../locales/portalTranslations';

const getReports = pt => [
  { id: 'all', name: pt('caseRegister'), description: pt('misHelp'), icon: <InsertChartOutlined />, color: '#2563eb' },
  { id: 'open', name: pt('openRegister'), description: pt('pendingAction'), icon: <HourglassEmpty />, color: '#0891b2' },
  { id: 'decisions', name: pt('decisionRegister'), description: pt('decisionHelp'), icon: <AssignmentTurnedIn />, color: '#16a34a' },
  { id: 'reassigned', name: pt('reassignmentMis'), description: pt('reassigned'), icon: <SwapHoriz />, color: '#f59e0b' },
  { id: 'expired', name: pt('expiredCases'), description: pt('expiredCases'), icon: <ErrorOutline />, color: '#dc2626' },
];

const STATUS_COLORS = {
  Open: 'primary', Pending: 'primary', Processed: 'info', Reassigned: 'warning',
  Recommended: 'success', Approved: 'success', 'Not Recommended': 'error', Rejected: 'error', Expired: 'error',
};

const ReportsPage = () => {
  const theme = useTheme();
  const pt = usePortalText();
  const reports = getReports(pt);
  const today = toDateInput(new Date());
  const [cases, setCases] = useState([]);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reportType, setReportType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    caseService.getAllCases()
      .then((response) => setCases(response.data.cases || []))
      .catch(() => setError(pt('reportsError')))
      .finally(() => setLoading(false));
  }, []);

  const periodCases = useMemo(() => filterCasesByDate(cases, from, to), [cases, from, to]);
  const reportCases = useMemo(() => periodCases.filter((item) => {
    if (reportType === 'open') return ['Open', 'Pending'].includes(item.status);
    if (reportType === 'decisions') return ['Recommended', 'Approved', 'Not Recommended', 'Rejected'].includes(item.status);
    if (reportType === 'reassigned') return item.status === 'Reassigned';
    if (reportType === 'expired') return item.status === 'Expired';
    return true;
  }), [periodCases, reportType]);

  const setQuickPeriod = (days) => {
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    setFrom(toDateInput(start));
    setTo(today);
  };

  const decided = periodCases.filter((item) => ['Recommended', 'Approved', 'Not Recommended', 'Rejected'].includes(item.status)).length;
  const pending = periodCases.filter((item) => ['Open', 'Pending'].includes(item.status)).length;
  const reassigned = periodCases.filter((item) => item.status === 'Reassigned').length;
  const selectedReport = reports.find((report) => report.id === reportType) || reports[0];

  if (loading) return <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={750}>{pt('misReports')}</Typography>
        <Typography color="text.secondary">{pt('misHelp')}</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: 1.5 }}>
          <Box sx={{ mr: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>{pt('reportPeriod')}</Typography>
            <Typography variant="caption" color="text.secondary">{pt('defaultsToday')}</Typography>
          </Box>
          <TextField label={pt('from')} type="date" size="small" value={from} onChange={(event) => setFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label={pt('to')} type="date" size="small" value={to} onChange={(event) => setTo(event.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: from }} />
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <Chip label={pt('today')} clickable color={from === today && to === today ? 'primary' : 'default'} onClick={() => setQuickPeriod(1)} />
            <Chip label={pt('last7')} clickable onClick={() => setQuickPeriod(7)} />
            <Chip label={pt('last30')} clickable onClick={() => setQuickPeriod(30)} />
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={<Download />}
            disabled={!reportCases.length}
            onClick={() => downloadCasesCsv(reportCases, `motor-inspection-${reportType}-${from}-to-${to}.csv`)}
          >
            {pt('exportCsv')}
          </Button>
        </Box>
      </Paper>

      {from > to && <Alert severity="warning" sx={{ mb: 2 }}>{pt('dateWarning')}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        {[
          { label: pt('casesCreated'), value: periodCases.length, icon: <CalendarToday />, color: theme.palette.primary.main },
          { label: pt('pendingAction'), value: pending, icon: <HourglassEmpty />, color: '#0891b2' },
          { label: pt('decisionsCompleted'), value: decided, icon: <CheckCircleOutline />, color: theme.palette.success.main },
          { label: pt('reassigned'), value: reassigned, icon: <SwapHoriz />, color: theme.palette.warning.main },
        ].map((item) => (
          <Paper key={item.label} variant="outlined" sx={{ p: 2.25, borderRadius: 2.5, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box><Typography variant="body2" color="text.secondary">{item.label}</Typography><Typography variant="h4" fontWeight={750} sx={{ mt: 0.5 }}>{item.value}</Typography></Box>
            <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', bgcolor: alpha(item.color, 0.1), color: item.color, borderRadius: 2 }}>{item.icon}</Box>
          </Paper>
        ))}
      </Box>

      <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1.5 }}>{pt('availableReports')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' }, gap: 1.5, mb: 3 }}>
        {reports.map((report) => {
          const selected = reportType === report.id;
          return (
            <Paper
              key={report.id}
              component="button"
              type="button"
              onClick={() => setReportType(report.id)}
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 138,
                textAlign: 'left',
                borderRadius: 2.5,
                cursor: 'pointer',
                borderColor: selected ? report.color : 'divider',
                bgcolor: selected ? alpha(report.color, 0.05) : 'background.paper',
                font: 'inherit',
                '&:hover': { borderColor: report.color },
              }}
            >
              <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', bgcolor: alpha(report.color, 0.1), color: report.color, borderRadius: 1.5, mb: 1.25 }}>{report.icon}</Box>
              <Typography variant="subtitle2" fontWeight={700}>{report.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, lineHeight: 1.45 }}>{report.description}</Typography>
            </Paper>
          );
        })}
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{selectedReport.name}</Typography>
            <Typography variant="body2" color="text.secondary">{reportCases.length} matching record{reportCases.length === 1 ? '' : 's'} • {from} to {to}</Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel>{pt('reportType')}</InputLabel>
            <Select value={reportType} label={pt('reportType')} onChange={(event) => setReportType(event.target.value)}>
              {reports.map((report) => <MenuItem key={report.id} value={report.id}>{report.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small" aria-label={`${selectedReport.name} preview`}>
            <TableHead>
              <TableRow>
                <TableCell>{pt('createdColumn')}</TableCell><TableCell>{pt('referenceNumber')}</TableCell>
                <TableCell>{pt('vehicle')}</TableCell><TableCell>{pt('reasonColumn')}</TableCell>
                <TableCell>{pt('customer')}</TableCell><TableCell>{pt('status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportCases.slice(0, 100).map((item) => (
                <TableRow hover key={item.case_id || item.web_reference_number}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(item.created_at || item.updated_at).toLocaleDateString()}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={650} color="primary.main">{item.web_reference_number}</Typography></TableCell>
                  <TableCell>{[item.make, item.model].filter(Boolean).join(' ') || pt('notSpecified')}<Typography variant="caption" color="text.secondary" display="block">{item.vehicle_plate || pt('noPlate')}</Typography></TableCell>
                  <TableCell>{item.reason_for_inspection || pt('notSpecified')}</TableCell>
                  <TableCell>{item.customer_email || pt('notProvided')}</TableCell>
                  <TableCell><Chip label={item.status || pt('unknown')} size="small" color={STATUS_COLORS[item.status] || 'default'} variant="outlined" /></TableCell>
                </TableRow>
              ))}
              {!reportCases.length && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 7 }}><Typography color="text.secondary">{pt('noRecords')}</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {reportCases.length > 100 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5, textAlign: 'center' }}>Preview shows the first 100 records. CSV export includes all {reportCases.length} records.</Typography>}
      </Paper>
    </Box>
  );
};

export default ReportsPage;
